import { useCallback, useEffect, useRef, useState } from 'react'
import { getDirectorySuggestions } from 'actions/collaborativeTasks'

export const useDirectorySuggestions = (applicationUuid, query, debounceMs = 200) => {
    const [suggestions, setSuggestions] = useState([])
    const [loading, setLoading] = useState(false)
    const timerRef = useRef(null)
    // Monotonic id of the most recently dispatched request. Responses that
    // aren't the latest are discarded so an older query can't overwrite the
    // suggestions for the current query (and, in turn, the EXT warning derived
    // from them).
    const requestSeqRef = useRef(0)

    const fetchData = useCallback(async (q) => {
        if (!applicationUuid || !q || q.trim().length < 1) {
            requestSeqRef.current += 1   // invalidate any in-flight request
            setSuggestions([])
            setLoading(false)
            return
        }
        const seq = ++requestSeqRef.current
        setLoading(true)
        try {
            const res = await getDirectorySuggestions(applicationUuid, q.trim())()
            if (seq !== requestSeqRef.current) return
            const payload = res?.response || res
            setSuggestions(Array.isArray(payload) ? payload : [])
        } catch (e) {
            if (seq !== requestSeqRef.current) return
            setSuggestions([])
        } finally {
            if (seq === requestSeqRef.current) setLoading(false)
        }
    }, [applicationUuid])

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => fetchData(query), debounceMs)
        return () => { if (timerRef.current) clearTimeout(timerRef.current) }
    }, [query, debounceMs, fetchData])

    return { suggestions, loading }
}
